import React, { useCallback, useMemo, useState } from 'react';
import { CloudDownload } from '@mui/icons-material';
import { Box, Button, Stack, Typography } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { useSelector } from 'react-redux';
import { downloadFinalReportPdf } from 'dashboard409a/services/dashboard';
import { RootStateType } from 'dashboard409a/states/store';
import { useDocuments } from 'services/hooks';
import theme from 'theme';
import { useAccordionReportHeaderStyles } from './styles';
import { AccordionReportHeaderProps } from './types';

const AccordionReportHeader: React.FC<AccordionReportHeaderProps> = ({ reportName }) => {
  const classes = useAccordionReportHeaderStyles(theme);
  const { currentMeasurementDate, currentValuationScalarOpinion } = useSelector((state: RootStateType) => ({
    currentMeasurementDate: state.global.currentMeasurementDate,
    currentValuationScalarOpinion: state.valuation.currentValuationScalarOpinion,
  }));
  const { downloadValuationReport } = useDocuments(currentMeasurementDate);

  const finalReportPdfReference = currentValuationScalarOpinion?.final_report_pdf_reference;
  const reportCmdId = currentValuationScalarOpinion?.report_date?.id;
  const [isDownloadingReport, setIsDownloadingReport] = useState(false);

  const handleClick = useCallback(async () => {
    if (currentValuationScalarOpinion?.valuation_report) {
      await downloadValuationReport(currentValuationScalarOpinion.valuation_report);
    }
  }, [currentValuationScalarOpinion, downloadValuationReport]);

  const handleDownloadFinalReport = useCallback(async () => {
    if (!reportCmdId) {
      return;
    }
    setIsDownloadingReport(true);
    try {
      const { signed_url: signedUrl } = await downloadFinalReportPdf(reportCmdId);
      if (signedUrl) {
        window.open(signedUrl, '_blank');
      }
    } catch {
      enqueueSnackbar('Unable to download the 409A report. Please try again.', { variant: 'error' });
    } finally {
      setIsDownloadingReport(false);
    }
  }, [reportCmdId]);

  const downloadIcon = useMemo(() => <CloudDownload />, []);

  return (
    <Box mt={4} mb={3} display="flex" justifyContent="space-between" alignItems="center" width="100%">
      <div>
        <Typography variant="h6" component="h6" gutterBottom>
          {reportName} Report
        </Typography>
        <Typography variant="body2" component="p">
          Overview of the documentation analyzed
        </Typography>
      </div>
      <Stack direction="row" spacing={1} alignItems="center">
        {finalReportPdfReference && (
          <Button
            id="download-409a-report-button"
            data-testid="download-409a-report-button"
            variant="outlined"
            color="primary"
            startIcon={downloadIcon}
            disabled={isDownloadingReport}
            onClick={handleDownloadFinalReport}
            className={classes.button}>
            409A Report
          </Button>
        )}
      </Stack>
    </Box>
  );
};

export default AccordionReportHeader;
